// =============================================================================
// REVIEWS SECTION — Customer Testimonials & Google Maps Reviews Block
// =============================================================================
// Displays high-converting customer reviews with:
// - Overall rating summary badge (stars, score, count)
// - Link to view all reviews on Google Maps
// - Top-N filtered review cards sorted by rating & quality
// - Fully responsive, zero horizontal overflow, and WCAG AA tap targets
// - Pure static rendering with zero third-party tracking scripts (RGPD / GDPR safe)
// =============================================================================

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Restaurant, ReviewItem } from '@webzite/content-schema';
import { StarIcon, QuoteIcon, VerifiedBadgeIcon, GoogleIcon, ExternalLinkIcon } from './icons.js';
import { CTAButton } from './CTAButton.js';
import './ReviewsSection.css';

export interface ReviewsSectionProps {
  /** The restaurant data containing reviews and feature toggles */
  restaurant: Restaurant;
  /** Maximum number of top reviews to display (default: 3) */
  maxItems?: number;
  /** Minimum star rating to include in the display (default: 4.5) */
  minRating?: number;
  /** Custom CSS class name */
  className?: string;
}

/** Helper to generate monogram initials from author name (e.g. "Jean Dupont" -> "JD") */
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return 'C';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] ?? ''}${parts[parts.length - 1]?.[0] ?? ''}`.toUpperCase();
}

export function ReviewsSection({
  restaurant,
  maxItems = 3,
  minRating = 4.0,
  className = '',
}: ReviewsSectionProps) {
  const { t } = useTranslation();
  const reviewsConfig = restaurant.reviews;

  // Top-N Filtering logic:
  // 1. Filter out reviews below minRating
  // 2. Sort by rating descending, then text length descending (richer comments first)
  // 3. Take top N
  const topReviews = useMemo<ReviewItem[]>(() => {
    if (!reviewsConfig || !reviewsConfig.items) return [];
    return [...reviewsConfig.items]
      .filter((r) => r.rating >= minRating)
      .sort((a, b) => {
        if (b.rating !== a.rating) return b.rating - a.rating;
        return (b.text?.length ?? 0) - (a.text?.length ?? 0);
      })
      .slice(0, maxItems);
  }, [reviewsConfig, minRating, maxItems]);

  // Don't render if feature is disabled or no reviews exist
  if (!restaurant.features.reviews || !reviewsConfig || topReviews.length === 0) {
    return null;
  }

  const { summary } = reviewsConfig;

  return (
    <section
      id="reviews"
      className={`section reviews-section ${className}`}
      aria-labelledby="reviews-heading"
    >
      <div className="container">
        {/* Section Header */}
        <div className="text-center reviews-header">
          <h2 id="reviews-heading" className="reviews-title">
            {t('reviews.heading', 'Avis Clients')}
          </h2>
          <p className="reviews-subtitle">
            {t('reviews.subheading', 'Ce que nos clients disent de nous')}
          </p>

          {/* Rating Summary Bar */}
          <div className="reviews-summary-card">
            <div className="reviews-summary-rating">
              <span
                className="reviews-summary-score"
                aria-label={`Note de ${summary.rating} sur 5`}
              >
                {summary.rating.toFixed(1)}
              </span>
              <div className="reviews-stars-row" aria-hidden="true">
                {[1, 2, 3, 4, 5].map((star) => (
                  <StarIcon
                    key={star}
                    className="reviews-star-icon reviews-star-filled"
                    fill="currentColor"
                  />
                ))}
              </div>
            </div>

            <div className="reviews-summary-meta">
              <div className="reviews-platform-badge">
                <GoogleIcon className="reviews-google-icon" width="16" height="16" />
                <span className="reviews-platform-name">Google Reviews</span>
              </div>
              <span className="reviews-count-text">
                {t('reviews.basedOn', {
                  count: summary.reviewCount,
                  defaultValue: `basé sur ${summary.reviewCount}+ avis vérifiés`,
                })}
              </span>
            </div>

            {summary.url && (
              <div className="reviews-summary-action">
                <CTAButton
                  as="link"
                  href={summary.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="secondary"
                  className="reviews-cta-btn"
                >
                  <GoogleIcon width="16" height="16" />
                  <span>{t('reviews.viewAllGoogle', 'Voir sur Google Maps')}</span>
                  <ExternalLinkIcon width="14" height="14" />
                </CTAButton>
              </div>
            )}
          </div>
        </div>

        {/* Top-N Review Cards Grid */}
        <div className="grid-auto reviews-cards-grid" role="list">
          {topReviews.map((review) => (
            <article
              key={review.id}
              className={`reviews-card ${review.highlight ? 'reviews-card-highlight' : ''}`}
              role="listitem"
            >
              {/* Header: Stars & Quote Icon */}
              <div className="reviews-card-header">
                <div className="reviews-stars-row" aria-label={`${review.rating} étoiles sur 5`}>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <StarIcon
                      key={i}
                      className={`reviews-star-icon ${
                        i < Math.round(review.rating) ? 'reviews-star-filled' : 'reviews-star-empty'
                      }`}
                      fill="currentColor"
                    />
                  ))}
                </div>
                <QuoteIcon className="reviews-quote-icon" aria-hidden="true" />
              </div>

              {/* Review Text */}
              <blockquote className="reviews-quote-text">
                <p>"{review.text}"</p>
              </blockquote>

              {/* Reviewer Profile Row */}
              <div className="reviews-author-row">
                <div className="reviews-avatar-circle" aria-hidden="true">
                  {review.authorPhoto ? (
                    <img
                      src={review.authorPhoto}
                      alt=""
                      className="reviews-avatar-img"
                      loading="lazy"
                    />
                  ) : (
                    <span className="reviews-avatar-initials">{getInitials(review.author)}</span>
                  )}
                </div>

                <div className="reviews-author-info">
                  <div className="reviews-author-name-row">
                    <span className="reviews-author-name">{review.author}</span>
                    <span
                      className="reviews-verified-pill"
                      title={t('reviews.verifiedGoogle', 'Avis Google vérifié')}
                    >
                      <VerifiedBadgeIcon width="12" height="12" />
                      <span>{t('reviews.verifiedLabel', 'Vérifié')}</span>
                    </span>
                  </div>
                  {review.date && <span className="reviews-date-text">{review.date}</span>}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
